import { useState } from "react";

import FixedVideoBackground from "@/components/homepage/FixedVideoBackground";
import { HttpError, httpClient } from "@/api/http-client";

type Status = "idle" | "submitting" | "success" | "error";

interface SubscribeResponse {
  success: boolean;
  message: string;
  alreadySubscribed?: boolean;
}

// The members area lives on the `courses.` subdomain. From the apex we
// rewrite the current host to point at the subdomain so dev (aig.local),
// staging, and production all resolve correctly without hard-coding a
// hostname. Falls back to `/` on the server where window isn't available.
function getCoursesMembersUrl(path = "/"): string {
  if (typeof window === "undefined") return "/";
  const { protocol, hostname, port } = window.location;
  const host = hostname.startsWith("courses.")
    ? hostname
    : `courses.${hostname}`;
  return `${protocol}//${host}${port ? `:${port}` : ""}${path}`;
}

const CoursesLandingPage = () => {
  const [email, setEmail] = useState("");
  // Optional details — captured so we can address subscribers by name
  // in future broadcasts and segment by company. All three are explicitly
  // marked "optional" in the UI; backend treats empty strings as null.
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  // Honeypot — bots fill every text input. Real users never see this
  // field (it's positioned off-screen) and never type in it. The backend
  // silently rejects submissions where this is non-empty.
  const [website, setWebsite] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [responseMessage, setResponseMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === "submitting") return;
    setStatus("submitting");
    setResponseMessage("");

    try {
      const result = await httpClient.post<SubscribeResponse>(
        "/email-subscribers/subscribe",
        {
          email: email.trim(),
          source: "courses_landing",
          firstName: firstName.trim() || undefined,
          lastName: lastName.trim() || undefined,
          company: company.trim() || undefined,
          website,
        },
      );
      setStatus("success");
      setResponseMessage(result.message || "Thanks! You're on the list.");
      setEmail("");
      setFirstName("");
      setLastName("");
      setCompany("");
      setWebsite("");
    } catch (err) {
      setStatus("error");
      // Surface the server's actual validation message when it's a 4xx
      // (e.g. Fastify's "body/email must match format \"email\""), so
      // the user knows what to fix. Generic message for true unknowns.
      if (err instanceof HttpError) {
        const body = err.body as { message?: string } | undefined;
        if (err.status === 429) {
          setResponseMessage(
            body?.message ||
              "Too many attempts. Please wait a moment and try again.",
          );
        } else if (err.status >= 400 && err.status < 500) {
          setResponseMessage(
            body?.message ||
              "That email doesn't look right. Please double-check and try again.",
          );
        } else {
          setResponseMessage(
            body?.message ||
              "Something went wrong on our end. Please try again in a moment.",
          );
        }
      } else {
        setResponseMessage(
          "Couldn't reach the server. Please check your connection and try again.",
        );
      }
    }
  };

  return (
    <>
      <FixedVideoBackground />

      <div className="relative min-h-screen py-16 space-y-16">
        <div className="max-w-[1600px] mx-auto px-0 sm:px-12">
          <section className="relative bg-white py-16 sm:py-24 overflow-hidden shadow-2xl rounded-none sm:rounded-3xl">
            {/* Top-right shortcut to the members area for visitors who
                already have an account on the courses subdomain. Lives
                inside the page (not the global nav) so unauthenticated
                marketing traffic still sees a clear path to sign in. */}
            <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-20 flex items-center gap-2 sm:gap-3">
              <a
                href={getCoursesMembersUrl("/signin")}
                className="text-sm sm:text-base font-semibold text-slate-700 hover:text-[#B87333] transition px-3 py-2"
              >
                Sign in
              </a>
              <a
                href={getCoursesMembersUrl("/my-courses")}
                className="text-sm sm:text-base font-bold text-white bg-slate-900 hover:bg-slate-800 transition px-4 py-2 rounded-xl"
              >
                Members area →
              </a>
            </div>

            <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 pt-12 sm:pt-0">
              <div className="flex justify-center mb-6">
                <img
                  src="/aig_consulting_logos_v4_darkLogoOnly.png"
                  alt="AI Genius Automations"
                  loading="eager"
                  className="h-20 sm:h-24 w-auto object-contain"
                />
              </div>
              <p className="text-sm uppercase tracking-widest text-slate-500 font-semibold mb-4 text-center">
                Coming Soon
              </p>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 leading-tight mb-6 text-center">
                AI Genius Courses and Certificates
              </h1>
              <p className="text-lg text-slate-600 font-semibold mb-10 text-center">
                We're building a courses platform covering AI education from the
                ground up — for individuals, founders, and the teams they work
                with. Signups below get notified as lessons go live.
              </p>

              <div className="grid sm:grid-cols-3 gap-6 mb-12 text-center">
                <div className="p-5 rounded-2xl bg-slate-50 border border-slate-100">
                  <p className="font-bold text-slate-900 mb-1">
                    General AI Education
                  </p>
                  <p className="text-sm text-slate-600">
                    Foundational material on how modern AI works, what it can
                    and can't do, and how to think clearly about using it.
                  </p>
                </div>
                <div className="p-5 rounded-2xl bg-slate-50 border border-slate-100">
                  <p className="font-bold text-slate-900 mb-1">
                    AI for entrepreneurs
                  </p>
                  <p className="text-sm text-slate-600">
                    Topics aimed at founders and solo operators — finding
                    leverage, evaluating tools, and the workflows that actually
                    move a small business forward.
                  </p>
                </div>
                <div className="p-5 rounded-2xl bg-slate-50 border border-slate-100">
                  <p className="font-bold text-slate-900 mb-1">
                    AI for organizations & teams
                  </p>
                  <p className="text-sm text-slate-600">
                    Material on rolling AI out across an organization —
                    enablement, governance, and the team-level patterns we see
                    working in practice.
                  </p>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-10">
                <h2 className="text-2xl sm:text-3xl font-black text-slate-900 mb-3 text-center">
                  Get notified when we launch
                </h2>
                <p className="text-slate-600 mb-8 text-center">
                  Drop your email and we'll tell you when the first lessons are
                  live and how to get in early.
                </p>

                {status === "success" && (
                  <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-800 font-semibold text-center">
                    {responseMessage}
                  </div>
                )}

                {status === "error" && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-800 font-semibold text-center">
                    {responseMessage}
                  </div>
                )}

                <form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-3 max-w-xl mx-auto"
                >
                  {/* Honeypot field — visually hidden but present in DOM.
                      Real users never see or focus this. */}
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      left: "-10000px",
                      top: "auto",
                      width: "1px",
                      height: "1px",
                      overflow: "hidden",
                    }}
                  >
                    <label htmlFor="website">Website (leave this empty)</label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                    />
                  </div>

                  {/* Row 1: first / last name (optional, side-by-side on sm+) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label
                        htmlFor="courses-first-name"
                        className="block text-xs font-semibold text-slate-500 mb-1"
                      >
                        First name{" "}
                        <span className="font-normal">(optional)</span>
                      </label>
                      <input
                        type="text"
                        id="courses-first-name"
                        name="firstName"
                        autoComplete="given-name"
                        maxLength={200}
                        placeholder="Jane"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        disabled={status === "submitting"}
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 bg-white focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 outline-none text-slate-900 placeholder:text-slate-400 disabled:opacity-60"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="courses-last-name"
                        className="block text-xs font-semibold text-slate-500 mb-1"
                      >
                        Last name{" "}
                        <span className="font-normal">(optional)</span>
                      </label>
                      <input
                        type="text"
                        id="courses-last-name"
                        name="lastName"
                        autoComplete="family-name"
                        maxLength={200}
                        placeholder="Doe"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        disabled={status === "submitting"}
                        className="w-full px-4 py-3 rounded-xl border border-slate-300 bg-white focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 outline-none text-slate-900 placeholder:text-slate-400 disabled:opacity-60"
                      />
                    </div>
                  </div>

                  {/* Row 2: company (optional, full-width) */}
                  <div>
                    <label
                      htmlFor="courses-company"
                      className="block text-xs font-semibold text-slate-500 mb-1"
                    >
                      Company <span className="font-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      id="courses-company"
                      name="company"
                      autoComplete="organization"
                      maxLength={200}
                      placeholder="Acme Inc."
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      disabled={status === "submitting"}
                      className="w-full px-4 py-3 rounded-xl border border-slate-300 bg-white focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 outline-none text-slate-900 placeholder:text-slate-400 disabled:opacity-60"
                    />
                  </div>

                  {/* Row 3: email + submit (required) */}
                  <div>
                    <label
                      htmlFor="courses-email"
                      className="block text-xs font-semibold text-slate-500 mb-1"
                    >
                      Email
                    </label>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input
                        type="email"
                        id="courses-email"
                        name="email"
                        required
                        autoComplete="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={status === "submitting"}
                        className="flex-1 px-4 py-3 rounded-xl border border-slate-300 bg-white focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 outline-none text-slate-900 placeholder:text-slate-400 disabled:opacity-60"
                      />
                      <button
                        type="submit"
                        disabled={status === "submitting" || !email.trim()}
                        className="px-6 py-3 rounded-xl bg-slate-900 text-white font-bold hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed transition"
                      >
                        {status === "submitting" ? "Submitting…" : "Notify me"}
                      </button>
                    </div>
                  </div>
                </form>

                <p className="text-xs text-slate-500 text-center mt-4">
                  We'll only use this address to send course updates. See our{" "}
                  <a href="/privacy" className="underline hover:text-slate-700">
                    privacy policy
                  </a>
                  .
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default CoursesLandingPage;
